"use client";

import Link from "next/link";
import Image from "next/image";
import { Images } from "@/assests";
import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowLeft, ArrowRight } from "@/components/icons";
import PostBreadcrumb from "@/components/ui/post-breadcrumb";
import { HeroGlow } from "@/components/ui";
import { type ResearchPostProps } from "./types";
import { getCategoryColor } from "./data";
import { FREE_TRIAL_URL, FREE_TRIAL_LABEL } from "@/lib/constants";
import { MAINTENANCE_PATH } from "@/lib/maintenance";
import { useMaintenanceMode } from "@/hooks/use-maintenance-mode";
import {
  KeyFindings,
  ResearchChart,
  CodeBlock,
  AuthorAvatar,
  TableOfContents,
  ShareButton,
} from "./parts";

export default function ResearchPostSection({ post }: ResearchPostProps) {
  const color = getCategoryColor(post.category);
  const prefersReducedMotion = useReducedMotion();
  const maintenanceMode = useMaintenanceMode();
  const trialHref = maintenanceMode ? MAINTENANCE_PATH : FREE_TRIAL_URL;

  return (
    <main id="main-content" tabIndex={-1}>
      {/* ── Article header ── */}
      <section className="relative overflow-y-clip pt-24">
        <HeroGlow align="left" />
        <div className="relative mx-auto flex max-w-7xl gap-16 px-4 sm:px-6 lg:px-8">
          <motion.div
            className="min-w-0 flex-1"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <PostBreadcrumb
              backHref="/research"
              backLabel="Research"
              current=""
              color={color}
              className="mb-6"
            />

            <div className="mb-5 flex flex-wrap items-center gap-3">
              <span
                className="rounded-full px-3 py-1 text-xs font-semibold"
                style={{ color, background: color + "18" }}
              >
                {post.category}
              </span>
            </div>

            <h1 className="text-foreground mb-6 text-3xl leading-tight font-black tracking-tight sm:text-4xl lg:text-5xl">
              {post.title}
            </h1>

            <div className="border-border flex flex-wrap items-center justify-between gap-4 border-b pb-8">
              <div className="flex flex-wrap items-center gap-3">
                <AuthorAvatar initial={post.authorInitial} color={color} />
                <div>
                  <p className="text-foreground text-sm font-semibold">{post.author}</p>
                  <p className="text-muted-foreground text-xs">
                    {post.date} · {post.readTime}
                  </p>
                </div>
              </div>
              <ShareButton title={post.title} />
            </div>
          </motion.div>
          <div className="hidden w-52 shrink-0 xl:block" aria-hidden="true" />
        </div>
      </section>

      {/* ── Article body + ToC ── */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex items-start gap-16">
          <motion.article
            initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="prose prose-invert max-w-none min-w-0 flex-1"
            style={{
              ["--tw-prose-body" as string]: "var(--muted-foreground)",
              ["--tw-prose-headings" as string]: "var(--foreground)",
              ["--tw-prose-lead" as string]: "var(--muted-foreground)",
            }}
          >
            <div className="border-foreground/6 mb-10 overflow-hidden rounded-2xl border">
              <Image
                src={Images.blogHeroWave}
                alt={post.title}
                width={1200}
                height={480}
                sizes="(min-width: 1024px) 800px, 100vw"
                className="h-auto w-full object-cover"
                loading="eager"
              />
            </div>

            <p className="text-muted-foreground mb-8 text-lg leading-relaxed font-medium">
              {post.excerpt}
            </p>

            <h2
              id="methodology"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Methodology &amp; test setup
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              This study ran{" "}
              <strong className="text-foreground">300,000 requests per proxy type</strong> across
              500+ unique target domains over a 90-day period from January to March 2025. Targets
              were segmented into three tiers based on their anti-bot sophistication: Tier-1 (no
              protection), Tier-2 (basic fingerprinting), and Tier-3 (advanced fingerprinting +
              behavioral analysis).
            </p>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              All requests were made using a standardized headless Chromium instance with
              playwright-stealth patches applied. The only variable changed between test runs was
              the proxy configuration — type, rotation strategy, and pool size. Infrastructure was
              identical across all test groups.
            </p>

            <CodeBlock
              lang="python"
              code={`# Test harness — simplified
import asyncio
import random
from dataclasses import dataclass, field
from playwright.async_api import async_playwright
from playwright_stealth import stealth_async

@dataclass
class TestConfig:
    proxy_type: str          # "mobile" | "residential" | "datacenter" | "isp"
    rotation:   str          # "per_request" | "session" | "sticky_5min"
    pool_size:  int
    targets:    list[str] = field(default_factory=list)

async def run_test(config: TestConfig, n_requests: int = 1000) -> dict:
    results = {"success": 0, "blocked": 0, "error": 0}
    pool = get_proxy_pool(config.proxy_type, config.pool_size)

    async with async_playwright() as p:
        for _ in range(n_requests):
            proxy = get_next_proxy(pool, config.rotation)
            target = random.choice(config.targets)
            try:
                browser = await p.chromium.launch(proxy=proxy)
                page = await (await browser.new_context()).new_page()
                await stealth_async(page)
                resp = await page.goto(target, wait_until="networkidle")
                results["success" if resp.ok else "blocked"] += 1
                await browser.close()
            except Exception:
                results["error"] += 1
    return results`}
            />

            <h2
              id="key-findings"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Key findings
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              The data reveals a clear hierarchy: mobile IPs consistently outperform every other
              proxy type across all target tiers. The gap widens dramatically at Tier-3 targets —
              where mobile proxies achieve a 99.4% success rate versus 62.1% for datacenter proxies.
            </p>

            <KeyFindings />

            <h2
              id="proxy-types"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Proxy type breakdown
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Not all proxy types are equal. The chart below shows average success rates by proxy
              type across Tier-3 targets — the most demanding category. Mobile and residential
              proxies dominate; datacenter proxies fail on most sophisticated targets.
            </p>

            <ResearchChart />

            <h2
              id="target-tiers"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Target tier analysis
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Tier classification matters enormously. At Tier-1 targets (no anti-bot), even
              datacenter proxies achieve 98%+ success. But as target sophistication increases, the
              gap between mobile and datacenter proxies grows from ~1% to over 37 percentage points.
            </p>
            <ul className="mb-6 list-none space-y-3 pl-0">
              {[
                {
                  tier: "Tier-1",
                  desc: "Static HTML, no bot detection. All proxy types achieve 95%+ success rates. Cost optimization is the primary driver here.",
                },
                {
                  tier: "Tier-2",
                  desc: "Basic fingerprinting — JA3/JA4, User-Agent matching. Residential and mobile proxies maintain high success; datacenter begins to falter (~78%).",
                },
                {
                  tier: "Tier-3",
                  desc: "Full behavioral analysis, TLS fingerprinting, and IP reputation scoring. Only mobile and residential proxies maintain acceptable success rates.",
                },
              ].map((item) => (
                <li
                  key={item.tier}
                  className="border-border/60 bg-card flex gap-3 rounded-xl border p-4"
                >
                  <span
                    className="mt-0.5 h-2 w-2 shrink-0 rounded-full"
                    style={{ background: color, marginTop: 6 }}
                    aria-hidden="true"
                  />
                  <span className="text-muted-foreground text-sm leading-relaxed">
                    <strong className="text-foreground font-semibold">{item.tier}:</strong>{" "}
                    {item.desc}
                  </span>
                </li>
              ))}
            </ul>

            <h2
              id="cost-analysis"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Cost-per-success analysis
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Raw success rate tells only part of the story. When you factor in price-per-GB and
              normalize for successful requests only, mobile proxies are often <em>cheaper</em> than
              residential on Tier-3 targets — because fewer requests are wasted on blocks and
              retries.
            </p>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              For teams scraping Tier-3 targets at scale, switching from residential to mobile
              proxies reduced their effective cost-per-successful-request by 22% despite a higher
              nominal price-per-GB.
            </p>

            <h2
              id="recommendations"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Recommendations
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Based on our data, here is the recommended proxy strategy by use case:
            </p>
            <ul className="mb-6 list-none space-y-3 pl-0">
              {[
                {
                  use: "E-commerce scraping",
                  rec: "Mobile proxies with per-request rotation. Most major retailers are now Tier-3 targets.",
                },
                {
                  use: "Price monitoring",
                  rec: "Residential with session rotation (5–10 req). Balances cost and success at Tier-2 targets.",
                },
                {
                  use: "LLM training data",
                  rec: "Residential at scale with per-request rotation. Volume matters more than individual success rate.",
                },
                {
                  use: "Brand protection / SERP",
                  rec: "Mobile proxies. Search engines are the strictest Tier-3 targets in our dataset.",
                },
              ].map((item) => (
                <li
                  key={item.use}
                  className="border-border/60 bg-card flex gap-3 rounded-xl border p-4"
                >
                  <span
                    className="mt-0.5 h-2 w-2 shrink-0 rounded-full"
                    style={{ background: color, marginTop: 6 }}
                    aria-hidden="true"
                  />
                  <span className="text-muted-foreground text-sm leading-relaxed">
                    <strong className="text-foreground font-semibold">{item.use}:</strong>{" "}
                    {item.rec}
                  </span>
                </li>
              ))}
            </ul>

            <h2
              id="conclusion"
              className="text-foreground mt-12 mb-4 scroll-mt-24 text-2xl font-bold"
            >
              Conclusion
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              The proxy landscape in 2025 is more stratified than ever. As anti-bot systems grow
              more sophisticated, the gap between proxy types has widened — making proxy selection a
              first-order infrastructure decision, not an afterthought.
            </p>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Mobile IPs are the clear leaders for demanding targets. For teams scaling web data
              collection, the shift to mobile proxies with per-request rotation delivers the highest
              success rates and, counterintuitively, a lower effective cost-per-successful-request
              at Tier-3 targets.
            </p>

            <div className="border-border/60 mt-10 border-t pt-8">
              <Link
                href="/research"
                className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm font-medium transition-colors"
              >
                <ArrowLeft size={14} />
                Back to Research
              </Link>
            </div>
          </motion.article>

          <TableOfContents />
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="mx-auto max-w-4xl px-4 pb-24 sm:px-6 lg:px-8">
        <div
          className="border-border flex flex-col items-center justify-between gap-6 rounded-2xl border p-8 sm:flex-row sm:p-10"
          style={{
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--foreground) 3%, transparent) 0%, var(--background) 60%, rgba(16,185,129,0.12) 100%)",
          }}
        >
          <div>
            <h2 className="text-foreground mb-1 text-xl font-bold sm:text-2xl">
              Ready to put this research to work?
            </h2>
            <p className="text-muted-foreground text-sm">
              Power your data infrastructure with the proxies behind these benchmarks.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <Link
              href={trialHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground border-border hover:border-foreground/20 inline-flex items-center gap-2 rounded-lg border px-5 py-2.5 text-sm font-semibold transition-colors"
            >
              {FREE_TRIAL_LABEL}
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
